import { useEffect, useMemo, useState } from 'react';
import { ArrowUp, ChevronDown, Search } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useMenuData } from '../lib/useMenuData';
import { SITE } from '../data/site';
import { money, cn } from '../lib/util';
import { Diet } from '../components/Bits';
import { Img } from '../components/Img';
import type { MenuItem } from '../data/menu';

type TaggedItem = MenuItem & { subLabel?: string };
type Section = { id: string; title: string; kicker: string; blurb?: string; items: TaggedItem[] };

// "Flat" merges: raw sub-categories whose items get folded directly into a
// main category's item list, with NO dropdown and NO sub-label — they read
// as if they'd always been part of that category (e.g. Grilled Chicken ->
// Hyderabadi Specials).
interface FlatMerge { mainId: string; mergeIds: string[] }
const FLAT_MERGES: FlatMerge[] = [
  { mainId: 'hyderabadi-specials', mergeIds: ['grilled-chicken'] },
];

// "Dropdown" merges: a main category (which may or may not exist on its own)
// absorbs a set of raw sub-categories behind an in-page dropdown filter.
interface DropdownMerge { id: string; title: string; mergeIds: string[]; allLabel: string }
const DROPDOWN_MERGES: DropdownMerge[] = [
  {
    id: 'breakfast-8am-to-12pm',
    title: 'Breakfast (8AM to 12PM)',
    mergeIds: ['idli', 'bonda', 'wada', 'dosa', 'uthappam', 'pesarattu', 'ram-ki-bandi-dosa', 'non-veg-breakfast'],
    allLabel: 'All Breakfast Items',
  },
  {
    id: 'beverages',
    title: 'Beverages',
    mergeIds: ['fresh-fruit-juices', 'milkshakes', 'fuljar-soda', 'soda-float'],
    allLabel: 'All Beverages',
  },
  {
    id: 'appetizers',
    title: 'Appetizers',
    mergeIds: ['veg-appetizers', 'egg-appetizers', 'chicken-appetizers', 'mutton-appetizers-goat', 'seafood-appetizers'],
    allLabel: 'All Appetizers',
  },
  {
    id: 'curryosity',
    title: "Curry'osity",
    mergeIds: ['veg-entrees', 'chicken-entrees', 'murgh-musallam', 'mutton-entrees-goat', 'seafood-entrees'],
    allLabel: "All Curry'osity",
  },
  {
    id: 'kebab-sizzlers',
    title: 'Kebab Sizzlers',
    mergeIds: ['veg-sizzlers', 'non-veg-sizzlers'],
    allLabel: 'All Kebab Sizzlers',
  },
  {
    id: 'hyderabadi-biryanis',
    title: "Hyderabadi Biryani's",
    mergeIds: ['chicken-biryanis', 'mutton-biryanis', 'veg-biryanis'],
    allLabel: "All Biryani's",
  },
  {
    id: 'andhra-pulavs',
    title: "Andhra's Pulavs",
    mergeIds: ['veg-pulavs', 'mutton-pulavs', 'chicken-pulavs'],
    allLabel: "All Pulav's",
  },
];

export default function MenuPage() {
  const { sections: rawSections } = useMenuData();

  const sections = useMemo<Section[]>(() => {
    let pool: Section[] = rawSections
      .filter((s) => s.items.length > 0)
      .map((s) => ({ ...s, items: s.items.map((it) => ({ ...it }) as TaggedItem) }));

    // 1) Flat merges — fold items directly into the main category, no sub-label.
    for (const fm of FLAT_MERGES) {
      const mainIdx = pool.findIndex((s) => s.id === fm.mainId);
      const mergeSecs = fm.mergeIds.map((id) => pool.find((s) => s.id === id)).filter((s): s is Section => !!s);
      if (mainIdx === -1 && mergeSecs.length === 0) continue;
      const extraItems = mergeSecs.flatMap((s) => s.items);
      if (mainIdx !== -1 && extraItems.length > 0) {
        pool[mainIdx] = { ...pool[mainIdx], items: [...pool[mainIdx].items, ...extraItems] };
      }
      pool = pool.filter((s) => !fm.mergeIds.includes(s.id));
    }

    // 2) Dropdown merges — fold main + sub-categories into one section,
    // tagging non-main items with subLabel for the in-page dropdown filter.
    const built = new Map<string, Section>();
    const consumed = new Set<string>();
    for (const dm of DROPDOWN_MERGES) {
      const main = pool.find((s) => s.id === dm.id);
      const subs = dm.mergeIds.map((id) => pool.find((s) => s.id === id)).filter((s): s is Section => !!s);
      if (!main && subs.length === 0) continue;
      const directItems: TaggedItem[] = (main?.items ?? []).map((it) => ({ ...it }));
      const subItems: TaggedItem[] = subs.flatMap((s) => s.items.map((it) => ({ ...it, subLabel: s.title })));
      const items = [...directItems, ...subItems];
      if (items.length === 0) continue;
      built.set(dm.id, {
        id: dm.id,
        title: dm.title,
        kicker: main?.kicker ?? subs[0]?.kicker ?? '',
        blurb: main?.blurb ?? subs[0]?.blurb,
        items,
      });
      consumed.add(dm.id);
      dm.mergeIds.forEach((id) => consumed.add(id));
    }

    const result: Section[] = [];
    const inserted = new Set<string>();
    for (const s of pool) {
      if (consumed.has(s.id)) {
        const dm = DROPDOWN_MERGES.find((d) => d.id === s.id || d.mergeIds.includes(s.id));
        if (dm && built.has(dm.id) && !inserted.has(dm.id)) {
          result.push(built.get(dm.id)!);
          inserted.add(dm.id);
        }
        continue;
      }
      result.push(s);
    }
    for (const dm of DROPDOWN_MERGES) {
      if (built.has(dm.id) && !inserted.has(dm.id)) {
        result.push(built.get(dm.id)!);
        inserted.add(dm.id);
      }
    }
    return result;
  }, [rawSections]);

  // Sub-category dropdown options, in the order their categories first appear.
  function subcatsFor(sectionId: string) {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return [] as string[];
    const labels: string[] = [];
    for (const it of section.items) {
      if (it.subLabel && !labels.includes(it.subLabel)) labels.push(it.subLabel);
    }
    return labels;
  }

  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState('all');
  const [activeSub, setActiveSub] = useState('all');

  const activeDropdownMerge = useMemo(() => DROPDOWN_MERGES.find((d) => d.id === activeId), [activeId]);
  const activeSubcats = useMemo(
    () => (activeDropdownMerge ? subcatsFor(activeDropdownMerge.id) : []),
    [sections, activeDropdownMerge],
  );

  // Keep the active category valid if the underlying data changes.
  useEffect(() => {
    const validIds = new Set(['all', ...sections.map((s) => s.id)]);
    if (!validIds.has(activeId)) setActiveId('all');
  }, [sections, activeId]);

  // Reset the sub-filter whenever the top-level category changes.
  useEffect(() => {
    setActiveSub('all');
  }, [activeId]);

  const searching = query.trim().length > 0;

  const results = useMemo(() => {
    if (searching) {
      const q = query.trim().toLowerCase();
      return sections.flatMap((s) =>
        s.items
          .filter((it) => it.name.toLowerCase().includes(q) || (it.desc ?? '').toLowerCase().includes(q))
          .map((it) => ({ item: it, sectionTitle: s.title })),
      );
    }
    if (activeId === 'all') {
      return sections.flatMap((s) => s.items.map((it) => ({ item: it, sectionTitle: s.title })));
    }
    const active = sections.find((s) => s.id === activeId);
    let items: TaggedItem[] = active?.items ?? [];
    if (activeDropdownMerge && activeSub !== 'all') {
      items = items.filter((it) => it.subLabel === activeSub);
    }
    return items.map((it) => ({ item: it, sectionTitle: active?.title ?? '' }));
  }, [sections, activeId, query, searching, activeDropdownMerge, activeSub]);

  const activeSection = useMemo(() => {
    if (activeId === 'all') return null;
    return sections.find((s) => s.id === activeId) ?? null;
  }, [activeId, sections]);

  const pillItems = useMemo(
    () => [{ id: 'all', title: 'All' }, ...sections.map((s) => ({ id: s.id, title: s.title }))],
    [sections],
  );

  return (
    <div style={{ background: 'var(--color-surface)' }}>
      <Helmet>
        <title>Menu – Biryani, Mandi, Dosa & More | Biryanify Irving TX</title>
        <meta name="description" content="Full menu of Hyderabadi biryani, Arabian mandi, South Indian dosa, idli, kebabs and shawarma. 100+ dishes with prices — order online for pickup in Irving, TX." />
        <meta property="og:title" content="Menu – Biryani, Mandi, Dosa & More | Biryanify Irving TX" />
        <meta property="og:description" content="Full menu of Hyderabadi biryani, Arabian mandi, South Indian dosa, idli, kebabs and shawarma. 100+ dishes with prices — order online for pickup in Irving, TX." />
        <meta property="og:image" content="https://images.mydigimenu.com/media/t6mUbguWCr/30704-260626023005630143.jpg" />
        <meta property="og:url" content="https://biryaniify.us/menu" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Menu – Biryani, Mandi, Dosa & More | Biryanify Irving TX" />
        <meta name="twitter:description" content="Full menu of Hyderabadi biryani, Arabian mandi, South Indian dosa, idli, kebabs and shawarma. 100+ dishes with prices — order online for pickup in Irving, TX." />
        <meta name="twitter:image" content="https://images.mydigimenu.com/media/t6mUbguWCr/30704-260626023005630143.jpg" />
        <link rel="canonical" href="https://biryaniify.us/menu" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Biryanify Menu",
          "description": "Complete menu featuring Hyderabadi dum biryani, Arabian mandi, South Indian dosa, idli, kebabs, and more",
          "url": "https://biryaniify.us/menu",
          "mainEntity": {
            "@type": "Restaurant",
            "name": "Biryanify",
            "url": "https://biryaniify.us/",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "9400 N MacArthur Blvd",
              "addressLocality": "Irving",
              "addressRegion": "TX",
              "postalCode": "75063",
              "addressCountry": "US"
            }
          }
        }) }} />
      </Helmet>
      <h1 className="sr-only">Our Menu: Hyderabadi Biryani, Dosas & Mandi</h1>
      {/* Masthead — photographic banner */}
      <section className="relative min-h-[52vh] md:min-h-[58vh] flex flex-col justify-end overflow-hidden">
        <Img src="https://images.mydigimenu.com/media/t6mUbguWCr/30704-260626023005630143.jpg" alt="A spread of South Indian dishes" eager className="absolute inset-0" imgClassName="scale-105" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.6) 0%, rgba(20,13,8,0.2) 40%, rgba(20,13,8,0.88) 100%)' }} />
        <div className="wrap relative pb-10 pt-32 md:pb-12">
          <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>The Full Menu</p>
          <h1 className="font-display mt-3 text-white" style={{ fontSize: 'clamp(2.4rem, 6vw, 4.2rem)', lineHeight: 1, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}>
            Our Menu: Hyderabadi Biryani, Dosas & Mandi
          </h1>
          <p className="mt-4 max-w-xl" style={{ fontSize: '1.08rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
            Hyderabadi, South Indian and Arabian dishes across {sections.length} categories — biryani,
            mandi, dosa, kebabs, shawarma and more. Prices in USD.
          </p>
        </div>
      </section>

      {/* Sticky control bar: search, then scrollable category pills */}
      <div className="sticky top-[4.7rem] z-30" style={{ background: 'color-mix(in srgb, var(--color-surface) 92%, transparent)', backdropFilter: 'saturate(1.4) blur(12px)', borderBottom: '1px solid var(--color-line)' }}>
        <div className="wrap py-3 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <label className="relative flex-1 sm:max-w-xs">
              <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-muted)' }} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the menu…"
                className="w-full h-11 pl-10 pr-4 rounded-full text-[0.92rem] outline-none transition-shadow"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-line)', color: 'var(--color-ink)' }}
              />
            </label>

            {searching && (
              <span className="text-[0.85rem] shrink-0" style={{ color: 'var(--color-muted)' }}>
                {results.length} result{results.length === 1 ? '' : 's'} for “{query.trim()}”
              </span>
            )}
          </div>

          <CategoryPills
            items={pillItems}
            activeId={activeId}
            onSelect={(id) => { setActiveId(id); setQuery(''); }}
            disabled={searching}
          />

          {!searching && activeDropdownMerge && activeSubcats.length > 0 && (
            <label className="relative w-full sm:w-64">
              <select
                value={activeSub}
                onChange={(e) => setActiveSub(e.target.value)}
                className="w-full h-10 pl-4 pr-9 rounded-full text-[0.88rem] font-medium outline-none appearance-none"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-line)', color: 'var(--color-ink-2)' }}
              >
                <option value="all">{activeDropdownMerge.allLabel}</option>
                {activeSubcats.map((label) => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
              <ChevronDown size={15} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-muted)' }} />
            </label>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="wrap py-12 md:py-16">
        {!searching && (
          <div className="flex items-baseline gap-4 mb-6">
            <h2 className="font-display text-ink" style={{ fontSize: 'clamp(1.6rem, 3.6vw, 2.3rem)' }}>
              {activeId === 'all' ? 'All Dishes' : activeSection?.title ?? 'Menu'}
              {activeDropdownMerge && activeSub !== 'all' ? ` — ${activeSub}` : ''}
            </h2>
            <span className="h-px flex-1" style={{ background: 'var(--color-line)' }} />
          </div>
        )}
        {!searching && activeSection?.blurb && (
          <p className="-mt-3 mb-8 max-w-2xl text-ink-2" style={{ lineHeight: 1.6 }}>{activeSection.blurb}</p>
        )}

        {results.length === 0 ? (
          <div className="rounded-2xl p-10 text-center" style={{ background: 'var(--color-card-2)' }}>
            <p className="text-ink-2">No dishes match “{query.trim()}”. Try another search.</p>
          </div>
        ) : (
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {results.map(({ item, sectionTitle }, i) => (
              <li
                key={`${sectionTitle}-${item.name}-${i}`}
                className="group card overflow-hidden flex flex-col"
              >
                {(item.signature || item.weekend) && (
                  <div className="flex gap-1.5 px-4 pt-4">
                    {item.signature && (
                      <span className="text-[0.62rem] font-bold tracking-wider uppercase px-2 py-1 rounded-full" style={{ background: 'var(--color-on-accent)', color: 'var(--color-accent-deep)' }}>Signature</span>
                    )}
                    {item.weekend && (
                      <span className="text-[0.62rem] font-bold tracking-wider uppercase px-2 py-1 rounded-full" style={{ background: 'var(--color-on-accent)', color: 'var(--color-muted)' }}>Weekend</span>
                    )}
                  </div>
                )}
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start gap-2">
                    <Diet tag={item.tag} className="translate-y-[4px]" />
                    <span className="font-medium text-ink flex-1" style={{ fontSize: '1.02rem' }}>{item.name}</span>
                    <span className="tnum font-display shrink-0" style={{ fontSize: '1.02rem', color: 'var(--color-accent-deep)' }}>{money(item.price)}</span>
                  </div>
                  {searching && (
                    <p className="mt-1 text-[0.72rem] uppercase tracking-wide" style={{ color: 'var(--color-muted)' }}>{sectionTitle}</p>
                  )}
                  {item.desc && <p className="text-[0.88rem] text-muted mt-1.5 leading-snug">{item.desc}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Footnote */}
        <div className="mt-10 rounded-2xl p-6 text-center" style={{ background: 'var(--color-card-2)' }}>
          <p className="text-ink-2">
            Prices and availability may vary. For allergies or custom requests, please call{' '}
            <a href={SITE.phoneHref} className="font-semibold" style={{ color: 'var(--color-accent-deep)' }}>{SITE.phone}</a>.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[0.85rem] text-muted">
            <span className="inline-flex items-center gap-2"><Diet tag="veg" /> Vegetarian</span>
            <span className="inline-flex items-center gap-2"><Diet tag="egg" /> Contains egg</span>
            <span className="inline-flex items-center gap-2"><Diet tag="nonveg" /> Non-vegetarian</span>
          </div>
        </div>
      </div>

      <BackToTop />
    </div>
  );
}

function CategoryPills({
  items,
  activeId,
  onSelect,
  disabled,
}: {
  items: { id: string; title: string }[];
  activeId: string;
  onSelect: (id: string) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        '-mx-4 px-4 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none',
        disabled && 'opacity-50 pointer-events-none',
      )}
      style={{ scrollbarWidth: 'none' }}
    >
      {items.map((s) => {
        const isActive = s.id === activeId;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onSelect(s.id)}
            className="shrink-0 whitespace-nowrap h-10 px-4 rounded-full text-[0.88rem] font-medium transition-colors"
            style={
              isActive
                ? { background: 'var(--color-accent-deep)', color: 'var(--color-on-accent)' }
                : { background: 'var(--color-card)', border: '1px solid var(--color-line)', color: 'var(--color-ink-2)' }
            }
          >
            {s.title}
          </button>
        );
      })}
    </div>
  );
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 800);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
      className={cn('fixed bottom-6 right-6 z-40 grid place-items-center w-12 h-12 rounded-full transition-all duration-300', show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none')}
      style={{ background: 'var(--color-ink)', color: 'var(--color-ink-inverse)', boxShadow: 'var(--pe-shadow-lg)' }}
    >
      <ArrowUp size={20} />
    </button>
  );
}
